import { DocDto } from "@gap/backend/app-services/doc/models/DocDto"
import { OrgDto } from "@gap/backend/app-services/org/models/OrgDto"
import { GapError } from "@trpc-endpoints/trpc/error-handler/GapError"
import { pdfClient, pdfClientSilent } from "api/trpc-pdf/trpc-client"
import { makeAutoObservable } from "mobx"
import BooleanState from "reusable/mobx-helpers/BooleanState"
import Loader from "reusable/mobx-helpers/Loader"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { messageConfirm } from "reusable/mui/modals/message-confirm"
import { snackbarError } from "reusable/mui/snackbar/snackbar"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"

interface DocViewStoreProps {
   docId: string
   collated: boolean
   orgGuardStore: OrgGuardStore
}

export default class DocViewStore {
   sp
   loader = new Loader(true)
   userHasAccessToThisDocument = true
   client: Pick<OrgDto.entity, 'id' | 'name'> | undefined = undefined
   breadcrumbDocName = ''
   markdownDocument?: string
   originalMarkdownDocument = ''
   downloadLink = ''
   fileName = ''
   document: DocDto.outputDoc | DocDto.entity | undefined = undefined
   hasAnswers = false
   editState = new BooleanState(false)
   saveLoader = new Loader(false)
   documentDoesNotExists = false

   constructor(sp: DocViewStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get canEdit() {
      return this.document?.docTypeName === 'Delivery' && this.sp.orgGuardStore.featureToggles['session-record-edit']
   }

   revertChanges = async () => {
      if (await messageConfirm('Are you sure you want to revert back to the original document?', 'Confirm', { variant: 'destroy', confirmButtonTitle: 'Yes, revert changes' })) {
         this.editState.close()
         await this.loader.executeLoading(async () => {
            try {
               this.originalMarkdownDocument = await pdfClientSilent.revertDocMarkdownChanges.mutate({ docId: this.sp.docId })
               this.markdownDocument = this.originalMarkdownDocument
            } catch {
               snackbarError('Unable to save changes, please reload the page and try again.')
            }
         }, 'Reverting changes')
      }
   }

   toggleEdit = async () => {
      const isSaveAction = !!this.editState.opened
      if (isSaveAction) {
         this.originalMarkdownDocument = this.markdownDocument || ''
      }
      this.editState.toggle()
      if (isSaveAction) {
         try {
            await this.saveLoader.executeLoading(async () => {
               await pdfClientSilent.updateDocMarkdownChanges.mutate({ docId: this.sp.docId, markdown: this.markdownDocument || '' })
            })
         } catch {
            snackbarError('Unable to save changes, please reload the page and try again.')
         }
      }
   }

   discardChanges = async () => {
      if (await messageConfirm('Are you sure you want to discard the current changes?', 'Confirm ', { variant: 'destroy', confirmButtonTitle: 'Yes, discard changes' })) {
         this.editState.close()
         this.markdownDocument = this.originalMarkdownDocument
      }
   }

   updateMarkdown = (updatedMarkdown: string) => {
      this.markdownDocument = updatedMarkdown
   }

   downloadAsPdf = async () => {
      await this.loader.executeLoading(async () => {
         try {
            const { url } = await pdfClient.mdToPdf.mutate({ markdown: this.markdownDocument || '', footerText: this.client?.name, entityId: this.sp.docId, isPreview: true })
            window.open(url, '_blank')
         } catch {
            snackbarError('Unable to download document, please reload the page and try again.')
         }
      })
   }

   fetch = async () => {
      await this.loader.executeLoading(async () => {
         try {
            const docInfo = await pdfClientSilent.getDocMarkdown.query({ docId: this.sp.docId, collated: this.sp.collated })
            this.client = { id: docInfo.clientOrgId, name: docInfo.clientName }
            this.markdownDocument = docInfo.markdown
            this.originalMarkdownDocument = docInfo.markdown
            this.breadcrumbDocName = docInfo.breadcrumbDocName
            this.downloadLink = docInfo.downloadLink
            this.fileName = docInfo.fileName
            this.document = docInfo.doc
            this.hasAnswers = docInfo.hasAnswers
         } catch (error: any) {
            if (error?.data?.gapError?.message === GapError.messages.forbidden.noDocumentAccessUser) {
               this.userHasAccessToThisDocument = false
            } else if (error?.data?.gapError?.name === "GapError.notFound") {
               this.documentDoesNotExists = true
            } else {
               // Default error handling
               throw error
            }
         }
      })
   }
}

export const { Provider: DocViewStoreProvider, useProvidedStore: useDocViewStore } = createStoreContext(DocViewStore)
